import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { useAuth } from "@/features/auth/use-auth";
import { useVisibility } from "@/features/visibility/use-visibility";
import { Visible } from "@/features/visibility/visible";
import { getModuleItems, type SidebarItem, sidebarModules } from "@/lib/sidebar-config";

type DashboardItem = SidebarItem & {
	moduleId: string;
	moduleTitle: string;
	sidebarModuleVisId: string;
};

function getItemVisibilityId(moduleId: string, item: SidebarItem): string {
	if (item.id) return item.id;
	const lastSegment = item.href.split("/").filter(Boolean).pop() ?? item.href;
	return `sidebar.${moduleId}.${lastSegment}`;
}

function getAllDashboardItems(): DashboardItem[] {
	const result: DashboardItem[] = [];
	for (const mod of sidebarModules) {
		if (mod.isFavorites || mod.id === "pulpit") continue;
		for (const item of getModuleItems(mod)) {
			result.push({
				...item,
				moduleId: mod.id,
				moduleTitle: mod.title,
				sidebarModuleVisId: getItemVisibilityId(mod.id, item),
			});
		}
	}
	return result;
}

const dashboardItems = getAllDashboardItems();

function DashboardPage() {
	const { user } = useAuth();
	const { isVisible, isEditorMode } = useVisibility();

	const hasAnyVisibleCard = dashboardItems.some((item) => isVisible(item.sidebarModuleVisId));

	const grouped = new Map<string, DashboardItem[]>();
	for (const item of dashboardItems) {
		const list = grouped.get(item.moduleTitle) ?? [];
		list.push(item);
		grouped.set(item.moduleTitle, list);
	}

	return (
		<div className="p-6 space-y-6 max-md:space-y-4 max-md:p-3">
			<PageHeader title="Dashboard" description={`Witaj${user?.name ? `, ${user.name}` : ""}`} />
			{Array.from(grouped.entries()).map(([moduleTitle, items]) => {
				const hasVisibleItem = items.some((item) => isVisible(item.sidebarModuleVisId));
				if (!hasVisibleItem && !isEditorMode) return null;
				return (
					<div key={moduleTitle} className="space-y-3">
						<h2 className="text-sm font-semibold text-[var(--muted-foreground)] uppercase tracking-wide">
							{moduleTitle}
						</h2>
						<div className="grid grid-cols-1 gap-3 max-md:gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							{items.map((item) => {
								const Icon = item.icon;
								return (
									<Visible key={item.href} id={item.sidebarModuleVisId} label={item.title}>
										<Link
											to={item.href}
											className="flex items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 hover:border-[var(--primary)] transition-colors max-md:min-h-11 max-md:px-3 max-md:py-2.5"
										>
											{Icon && <Icon className="size-5 text-[var(--primary)]" />}
											<p className="text-sm font-medium text-[var(--foreground)]">{item.title}</p>
										</Link>
									</Visible>
								);
							})}
						</div>
					</div>
				);
			})}
			{!hasAnyVisibleCard && !isEditorMode && (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<Layout className="size-8 text-[var(--muted-foreground)]/30 mb-2" />
					<p className="text-sm text-[var(--muted-foreground)]">Brak dostępnych widżetów</p>
				</div>
			)}
		</div>
	);
}

export const Route = createFileRoute("/_auth/dashboard")({
	component: DashboardPage,
});
